'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter, useParams } from 'next/navigation';
import Image from 'next/image';
import {
  getAvailableHoles,
  AvailableHole,
  getBonusPeriodStatus,
  BonusPeriod,
} from '@/lib/api';
import Breadcrumbs from '@/components/Breadcrumbs';
import styles from './page.module.css';
import PowerHourBanner from '@/components/PowerHourBanner';
import HowToPlay from '@/components/HowToPlay';
import TermsAndConditions from '@/components/TermsAndConditions';
import { useTranslation } from 'next-i18next';

export default function DayPage() {
  const router = useRouter();
  const params = useParams();
  const day = params.day as string;
  const [holes, setHoles] = useState<AvailableHole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPowerHourActive, setIsPowerHourActive] = useState(false);
  const [bonusPeriod, setBonusPeriod] = useState<BonusPeriod | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { t, i18n } = useTranslation('common');
  useEffect(() => {
    const fetchHoles = async () => {
      try {
        const data = await getAvailableHoles();
        setHoles(data.data);
      } catch (err) {
        setError(t('Failed to load holes'));
        console.error('Error fetching holes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHoles();
  }, []);

  // Poll for power hour status every 5 seconds
  useEffect(() => {
    const checkPowerHourStatus = async () => {
      try {
        const status = await getBonusPeriodStatus();
        setBonusPeriod(status as BonusPeriod);
        setIsPowerHourActive(status.open);
      } catch (err) {
        console.error('Error checking power hour status:', err);
        setIsPowerHourActive(false);
      }
    };

    // Check immediately
    checkPowerHourStatus();

    // Set up polling interval
    intervalRef.current = setInterval(checkPowerHourStatus, 5000);

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.content}>
          <div>{t('Loading holes...')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.content}>
          <div>
            {t('Error:')} {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.backgroundPattern}></div>
      <div className={styles.content}>
        <Breadcrumbs
          items={[
            { label: t('Play'), href: '/' },
            { label: t('Day') + ' ' + day, isCurrent: true },
          ]}
        />
        {isPowerHourActive && (
          <div style={{ paddingTop: '20px', paddingBottom: '20px' }}>
            <PowerHourBanner
              multiplier={bonusPeriod?.multiplier || 1}
              countdown={bonusPeriod?.countdown || 0}
            />
          </div>
        )}
        <HowToPlay />
        <h1 className={styles.pageTitle}>
          {t('Day')} {day}
        </h1>
        {/* Pick a Hole Section */}
        <div className={styles.pickHoleSection}>
          <h2 className={styles.sectionTitle}>{t('Pick a hole to play:')}</h2>

          <div className={styles.holeCards}>
            {holes.map((hole, index) => {
              return (
                <div
                  key={index}
                  className={`${styles.holeCard} ${styles.clickable}`}
                  onClick={() => router.push(`/${day}/${hole.number}`)}
                >
                  {/* Power Hour Indicator */}
                  {isPowerHourActive && (
                    <div className={styles.powerHourIndicator}>
                      <Image
                        src="/theme-assets/houston/team-voting/lightning.png"
                        alt="Lightning"
                        width={20}
                        height={20}
                        className={styles.lightningIcon}
                      />
                      <span className={styles.powerHourText}>
                        {t('Double Points Active')}
                      </span>
                    </div>
                  )}

                  <div
                    className={styles.holeImage}
                    style={{ backgroundImage: `url(${hole.image})` }}
                    onError={() =>
                      console.error('Image failed to load:', hole.image)
                    }
                    onLoad={() =>
                      console.log('Image loaded successfully:', hole.image)
                    }
                  >
                    <div className={styles.holeOverlay}>
                      <h3 className={styles.holeNumber}>
                        {t('HOLE')} {hole.number}
                      </h3>
                      <p className={styles.holeName}>
                        {t('Par')} {hole.par}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className={styles.termsSection}>
          <TermsAndConditions />
        </div>
      </div>
    </div>
  );
}
